'use client';

import { Search, Download, RefreshCw, Columns } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { ColumnConfig } from '@/lib/integrations/types';

interface DataToolbarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  columns: ColumnConfig[];
  onColumnsChange: (columns: ColumnConfig[]) => void;
  onExport: () => void;
  onRefresh: () => void;
  loading?: boolean;
}

export function DataToolbar({
  searchValue,
  onSearchChange,
  columns,
  onColumnsChange,
  onExport,
  onRefresh,
  loading,
}: DataToolbarProps) {
  const toggleColumn = (key: string) => {
    onColumnsChange(
      columns.map((col) =>
        col.key === key ? { ...col, visible: !col.visible } : col
      )
    );
  };

  const showAllColumns = () => {
    onColumnsChange(columns.map((col) => ({ ...col, visible: true })));
  };

  const hideAllColumns = () => {
    onColumnsChange(columns.map((col) => ({ ...col, visible: false })));
  };

  return (
    <div className="flex items-center justify-between gap-4 py-4">
      <div className="relative flex-1 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
        <Input
          placeholder="Search all columns..."
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="flex items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <Columns className="size-4 mr-2" />
              Columns
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <div className="flex gap-2 px-2 py-1">
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-7"
                onClick={showAllColumns}
              >
                All
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-7"
                onClick={hideAllColumns}
              >
                None
              </Button>
            </div>
            <DropdownMenuSeparator />
            {columns.map((column) => (
              <DropdownMenuCheckboxItem
                key={column.key}
                checked={column.visible}
                onCheckedChange={() => toggleColumn(column.key)}
              >
                {column.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <Button variant="outline" size="sm" onClick={onExport}>
          <Download className="size-4 mr-2" />
          Export
        </Button>

        <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading}>
          <RefreshCw className={`size-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>
    </div>
  );
}
